/** What a money question is about: `article` goes before the noun where a sentence needs one. */
export interface PercentThing {
    article: string;
    noun: string;
}

/** English percent prompts (ADR 0051 part 3). The first seven are unchanged from the original inline text. */
export const percentPrompts = {
    convertToDecimal: (percent: number) => `${percent}% = ______ as a decimal`,
    convertToFraction: (percent: number) => `${percent}% = ______ as a fraction over 100`,
    convertToPercent: (decimalStr: string) => `${decimalStr} = ______%`,
    findThePercent: (part: number, base: number) => `${part} is what percent of ${base}?`,
    percentChangeRise: (base: number, percent: number) =>
        `A price of ${base} rises by ${percent}%. What is the new price?`,
    percentChangeFall: (base: number, percent: number) =>
        `A price of ${base} falls by ${percent}%. What is the new price?`,
    percentOf: (percent: number, base: number) => `What is ${percent}% of ${base}?`,

    // Money questions (ADR 0120 §6). Amounts and rates arrive already written
    // ("$28.00", "7.5"); the `$` and the dot are the same in every language.
    /** Things bought in a shop, for tax, discount and markup. */
    retailThings: [
        { article: 'a', noun: 'shirt' },
        { article: 'a', noun: 'jacket' },
        { article: 'a', noun: 'bike' },
        { article: 'a', noun: 'lamp' },
        { article: 'a', noun: 'backpack' },
        { article: 'a', noun: 'game' },
        { article: 'a', noun: 'pair of shoes' },
        { article: 'an', noun: 'umbrella' },
    ] as PercentThing[],
    /** Things a tip is left on. */
    tipThings: [
        { article: 'a', noun: 'meal' },
        { article: 'a', noun: 'haircut' },
        { article: 'a', noun: 'taxi ride' },
        { article: 'a', noun: 'pizza delivery' },
    ] as PercentThing[],
    salesTax: (thing: PercentThing, price: string, rate: string) =>
        `A ${price} ${thing.noun} has ${rate}% sales tax. What is the total cost?`,
    tip: (thing: PercentThing, bill: string, rate: string) =>
        `${thing.article === 'an' ? 'An' : 'A'} ${thing.noun} costs ${bill}. With a ${rate}% tip, what is the total?`,
    discount: (thing: PercentThing, price: string, rate: string) =>
        `A ${price} ${thing.noun} is ${rate}% off. What is the sale price?`,
    markup: (thing: PercentThing, cost: string, rate: string) =>
        `A store buys ${thing.article} ${thing.noun} for ${cost} and marks it up ${rate}%. What is the selling price?`,
    simpleInterest: (principal: string, rate: string, years: number, borrowed: boolean) =>
        borrowed
            ? `${principal} is borrowed at ${rate}% simple interest for ${years} years. How much interest is owed?`
            : `${principal} is saved at ${rate}% simple interest for ${years} years. How much interest does it earn?`,
};
